import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, MapPin, Eye, FileText, Plane, Ticket, ReceiptText, CreditCard, CheckCircle2, Copy, MoreVertical, Users, Share2, AlertTriangle, MessageCircle, RefreshCw } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import TripFinancials from './TripFinancials';
import CostBreakdown from '@/components/Trip/CostBreakdown';
import QuoteCurrencyMode from '@/components/Trip/QuoteCurrencyMode';
import AdditionalPaxEditor, { VisaStatusSelect } from '@/components/Trip/AdditionalPaxEditor';
import TravellerVisaDocs from '@/components/traveller/TravellerVisaDocs';
import FollowUpManager from '@/components/followup/FollowUpManager';
import AiBriefPanel from '@/components/ai/AiBriefPanel';
import ReassignTripDialog from '@/components/Trip/ReassignTripDialog';
import TripMobileHeader from '@/components/Trip/TripMobileHeader';
import SendWhatsAppDialog from '@/components/SendWhatsAppDialog';
import FlightTextQuoteDialog from '@/components/Trip/FlightTextQuoteDialog';
import HotelTextQuoteDialog from '@/components/Trip/HotelTextQuoteDialog';
import CancelTripDialog from '@/components/credits/CancelTripDialog';
import { buildTripWaContext, tripSuggestHint, resolveTripPaymentLink } from '@/utils/waContext';
import { waNumber } from '@/mobile/lib/ui';
import { waLink } from '@/utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

// Directive 3 (2026-09-21) — quote-family version history list. Shared by
// both the active (editable) and superseded (read-only) render branches.
const VersionHistoryPanel = ({ versions, currentTripId, onSelect }) => {
  if (!versions || versions.length === 0) return null;
  const sorted = [...versions].sort((a, b) => (b.quoteVersion || 1) - (a.quoteVersion || 1));
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="quote-version-panel">
      <Label className="text-sm font-semibold text-slate-700 mb-3 block">Quote Versions</Label>
      <div className="space-y-2">
        {sorted.map((v) => {
          const isActive = v.quoteStatus === 'active';
          const isCurrent = v.tripId === currentTripId;
          return (
            <button
              key={v.tripId}
              type="button"
              disabled={isCurrent}
              onClick={() => onSelect(v)}
              data-testid={`quote-version-row-${v.quoteVersion || 1}`}
              className={`w-full text-left flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm transition
                ${isActive ? 'border-green-300 bg-green-50/60' : 'border-slate-200 bg-white'}
                ${isCurrent ? 'ring-2 ring-teal-400 cursor-default' : 'cursor-pointer hover:border-slate-300 hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">v{v.quoteVersion || 1}</span>
                <span
                  data-testid={`quote-version-status-${v.quoteVersion || 1}`}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    isActive ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {isActive ? 'Active' : 'Superseded'}
                </span>
                {isCurrent && <span className="text-xs text-teal-600">viewing</span>}
              </div>
              <div className="text-xs text-slate-400 text-right">
                {v.createdAt && new Date(v.createdAt).toLocaleDateString('en-IN')}
                {v.supersededReason && (
                  <div className="text-slate-500 mt-0.5 max-w-[220px] truncate" title={v.supersededReason}>
                    "{v.supersededReason}"
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Phase 17.2 #4 — expired when expiryDate is past and the quote isn't terminal.
const isQuoteExpired = (q) => {
  if (!q || !q.expiryDate) return false;
  if (['confirmed', 'completed', 'cancelled'].includes((q.status || '').toLowerCase())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(q.expiryDate);
  return !isNaN(exp.getTime()) && exp < today;
};

const EditQuote = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canFx = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const [showReassign, setShowReassign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [linkedItinerary, setLinkedItinerary] = useState(null);
  const [flightTextOpen, setFlightTextOpen] = useState(false);
  const [hotelTextOpen, setHotelTextOpen] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [togglingPayment, setTogglingPayment] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waPaymentLink, setWaPaymentLink] = useState('');
  const [autoFetching, setAutoFetching] = useState(false);
  const [departureCities, setDepartureCities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  // Directive 3 (2026-09-21) — quote-family version history.
  const [familyVersions, setFamilyVersions] = useState([]);
  const [showSupersedeModal, setShowSupersedeModal] = useState(false);
  const [supersedeReason, setSupersedeReason] = useState('');
  const [supersedeError, setSupersedeError] = useState('');
  const [superseding, setSuperseding] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [policies, setPolicies] = useState({
    inclusions: '',
    exclusions: '',
    paymentPolicy: '',
    cancellationPolicy: '',
    termsConditions: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { setPaymentEnabled(!!quote?.paymentEnabled); }, [quote?.paymentEnabled]);

  const fetchData = async () => {
    try {
      const [quoteRes, citiesRes, destRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quotes/${id}`),
        axios.get(`${BACKEND_URL}/api/departure-cities`),
        axios.get(`${BACKEND_URL}/api/destinations`)
      ]);
      
      const quoteData = quoteRes.data;
      setQuote(quoteData);
      setHotels(quoteData.hotels || []);
      setDepartureCities(citiesRes.data.cities || []);
      setDestinations(destRes.data.destinations || []);
      
      if (quoteData.policies) {
        setPolicies(quoteData.policies);
      }

      // Directive 3 — fetch every version sharing this quote's family, if any.
      if (quoteData.quoteFamilyId) {
        try {
          const famRes = await axios.get(`${BACKEND_URL}/api/quotes?quoteFamilyId=${quoteData.quoteFamilyId}`);
          setFamilyVersions(famRes.data || []);
        } catch (err) {
          console.debug('Could not load quote family versions (non-critical):', err?.message || err);
        }
      } else {
        setFamilyVersions([]);
      }

      // Check if there's a linked itinerary
      if (quoteData.tripId) {
        try {
          const itinRes = await axios.get(`${BACKEND_URL}/api/itineraries?tripId=${quoteData.tripId}`);
          if (itinRes.data && itinRes.data.length > 0) {
            setLinkedItinerary(itinRes.data[0]);
          }
        } catch (err) {
          console.debug('No linked itinerary for trip (non-critical):', err?.message || err);
        }
      }
    } catch (error) {
      toast.error('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  // Directive 3 — supersede this active quote into a new tracked version.
  const handleSupersede = async () => {
    setSuperseding(true);
    setSupersedeError('');
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/trips/${id}/duplicate?mode=supersede`,
        supersedeReason.trim() ? { reason: supersedeReason.trim() } : {},
      );
      setShowSupersedeModal(false);
      setSupersedeReason('');
      navigate(`/quotes/${r.data.id}/edit`);
    } catch (error) {
      setSupersedeError(error?.response?.data?.detail || 'Failed to create a new version');
    } finally {
      setSuperseding(false);
    }
  };

  const handleQuoteUpdate = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    
    try {
      const res = await axios.put(`${BACKEND_URL}/api/quotes/${id}`, quote);
      setQuote(res.data);
      toast.success('Trip updated successfully!');
    } catch (error) {
      toast.error('Failed to update trip');
    } finally {
      setSaving(false);
    }
  };

  // Directive 2 (2026-09-21) — V1 dynamic multi-option quote helpers.
  const newOptionId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `opt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

  const addOption = () => {
    setQuote((q) => ({
      ...q,
      pricingOptions: [...(q.pricingOptions || []), {
        optionId: newOptionId(), label: '', summary: '', costItems: [], totals: null, isSelected: false, notes: null,
      }],
    }));
  };

  const updateOptionField = (optionId, field, value) => {
    setQuote((q) => ({
      ...q,
      pricingOptions: (q.pricingOptions || []).map((o) => (o.optionId === optionId ? { ...o, [field]: value } : o)),
    }));
  };

  const selectOption = (optionId) => {
    setQuote((q) => ({
      ...q,
      pricingOptions: (q.pricingOptions || []).map((o) => ({ ...o, isSelected: o.optionId === optionId })),
    }));
  };

  const removeOption = (optionId) => {
    setQuote((q) => ({
      ...q,
      pricingOptions: (q.pricingOptions || []).filter((o) => o.optionId !== optionId),
    }));
  };

  // Directive: copy phone/email without manual select+retype.
  const copyField = async (value, label) => {
    if (!value) { toast.error(`No ${label} on file for this trip`); return; }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label}`);
    }
  };

  // Directive: auto-fetch phone/email from the linked traveller instead of
  // requiring a manual lookup + retype on this page.
  const autoFetchTravellerContact = async () => {
    if (!quote?.clientUCC) { toast.error('No traveller linked to this trip yet'); return; }
    setAutoFetching(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${quote.clientUCC}`, { withCredentials: true });
      const t = r.data || {};
      setQuote((q) => ({ ...q, email: t.email || q.email, phone: t.phone || q.phone }));
      toast.success('Phone/email fetched from traveller profile');
    } catch {
      toast.error('Could not fetch traveller contact details');
    } finally {
      setAutoFetching(false);
    }
  };

  // Prompt C1 — re-derive this quote's pricing from the linked itinerary.
  const handleSyncPricing = async (force = false) => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/${id}/pricing/sync${force ? '?confirm=true' : ''}`);
      setQuote(r.data);
      toast.success('Pricing synced from itinerary');
    } catch (error) {
      if (error?.response?.status === 409) {
        const msg = error.response.data?.detail || 'This trip has vendor cost data — syncing will erase it.';
        if (window.confirm(msg + '\n\nProceed anyway?')) {
          await handleSyncPricing(true);
        }
        return;
      }
      toast.error(error?.response?.data?.detail || 'Failed to sync pricing');
    }
  };

  // Prompt C1 — disconnect from itinerary pricing for manual editing.
  const handleUnlockPricing = async () => {
    if (!window.confirm('Manual edits will disconnect this quote from itinerary pricing. Changes in the itinerary builder will no longer auto-update it. Continue?')) {
      return;
    }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/${id}/pricing/unlock`);
      setQuote(r.data);
      toast.info('Pricing unlocked for manual editing');
    } catch (error) {
      toast.error('Failed to unlock pricing');
    }
  };

  // Step 8 — open the clean branded client preview link in a NEW TAB (and copy
  // it as a convenience) instead of only copying it.
  const handleSharePreview = async () => {
    if (!linkedItinerary) {
      toast.error('Create itinerary first');
      return;
    }
    const url = linkedItinerary.public_url
      || `${window.location.origin}/${linkedItinerary.public_ref || linkedItinerary.id}/preview`;
    window.open(url, '_blank', 'noopener,noreferrer');
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Preview opened in a new tab · link copied');
    } catch {
      toast.success('Preview opened in a new tab');
    }
  };

  // Open the agent itinerary preview in a new browser tab (keeps the edit page).
  const openItineraryNewTab = () => {
    if (!linkedItinerary) return;
    window.open(`/itineraries/${linkedItinerary.id}/preview`, '_blank', 'noopener,noreferrer');
  };

  const handleTogglePayment = async () => {
    setTogglingPayment(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/payments/toggle/${quote.tripId}`);
      setPaymentEnabled(res.data.paymentEnabled);
      toast.success(res.data.paymentEnabled ? 'Payment enabled — "Pay Now" will appear on Client Portal' : 'Payment disabled');
    } catch { toast.error('Failed to toggle payment'); } finally { setTogglingPayment(false); }
  };

  const handleMarkCompleted = async () => {
    if (!window.confirm('Mark this trip as Completed?\nThis triggers an LTV recompute and runs a reconciliation check on the linked invoice / receipts.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/${id}/complete`);
      const rec = r.data?.reconciliation || {};
      const msg = rec.status === 'balanced'
        ? 'Trip completed · invoice & receipts balanced ✓'
        : rec.status === 'outstanding'
          ? `Trip completed · ₹${Math.round(Math.abs(rec.difference || 0)).toLocaleString('en-IN')} still outstanding`
          : `Trip completed · ₹${Math.round(Math.abs(rec.difference || 0)).toLocaleString('en-IN')} overpaid`;
      toast.success(msg);
      setQuote((q) => ({ ...q, status: 'completed', reconciliation: rec }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark completed');
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('Duplicate this trip as a new draft?\nReceipts, payment schedule and invoice number will be cleared.')) return;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/${id}/duplicate`);
      toast.success(`Duplicated as ${r.data?.displayTripId || r.data?.tripId || ''}`);
      navigate(`/quotes/${r.data?.id || r.data?._id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate');
    }
  };

  const handleAddHotel = async () => {
    const newHotel = {
      city: '',
      hotelName: '',
      roomType: '',
      mealPlan: 'CP',
      nights: 1
    };
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quotes/${id}/hotels`, newHotel);
      setHotels([...hotels, response.data]);
      toast.success('Hotel added');
    } catch (error) {
      toast.error('Failed to add hotel');
    }
  };

  const handleDeleteHotel = async (hotelId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/quotes/${id}/hotels/${hotelId}`);
      setHotels(hotels.filter(h => h.id !== hotelId));
      toast.success('Hotel deleted');
    } catch (error) {
      toast.error('Failed to delete hotel');
    }
  };

  const handleSavePolicies = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${id}/policies`, policies);
      toast.success('Policies saved successfully!');
    } catch (error) {
      toast.error('Failed to save policies');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  if (!quote) {
    return <div className="flex items-center justify-center h-96">Trip not found</div>;
  }

  // Directive 3 (2026-09-21) — a superseded quote is history: no edit
  // actions, no save, no action buttons except Back.
  if (quote.quoteStatus === 'superseded') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="superseded-quote-view">
        <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')} data-testid="superseded-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="text-xl font-heading font-semibold text-slate-900">
              {quote.destination || quote.guestName || 'Trip'}
            </h1>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
              Superseded · v{quote.quoteVersion || 1}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            This version is read-only — it was replaced by a newer quote{quote.supersededReason ? `: "${quote.supersededReason}"` : '.'}
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-slate-400">Guest</span><div className="font-medium">{quote.guestName || '—'}</div></div>
            <div><span className="text-slate-400">Trip ID</span><div className="font-medium">{quote.displayTripId || quote.tripId}</div></div>
            <div><span className="text-slate-400">Base Cost</span><div className="font-medium">{fmtINR(quote.baseCost)}</div></div>
            <div><span className="text-slate-400">Total Cost</span><div className="font-medium">{fmtINR(quote.totalCost)}</div></div>
          </div>
        </Card>
        <VersionHistoryPanel
          versions={familyVersions}
          currentTripId={quote.tripId}
          onSelect={(v) => navigate(`/quotes/${v.tripId}/edit`)}
        />
      </div>
    );
  }

  const itemsTotal = Array.isArray(quote.costItems)
    ? quote.costItems.reduce((s, it) => {
        const inr = (parseFloat(it.cost) || 0) * (parseFloat(it.exchangeRate) || ((it.currency || 'INR') === 'INR' ? 1 : 1));
        return s + inr + (parseFloat(it.margin) || 0);
      }, 0)
    : 0;
  // Prompt C1 — when pricing is synced from the itinerary, the trip's stored
  // baseCost/gstRate/tcsRate are authoritative (ignore any stale cost items).
  const synced = quote.pricing_synced_from === 'itinerary';
  const baseFromItems = synced
    ? (parseFloat(quote.baseCost) || 0)
    : ((Array.isArray(quote.costItems) && quote.costItems.length > 0) ? itemsTotal : (parseFloat(quote.baseCost) || 0));
  // TCS preview math — mirrors backend `_resolve_tcs_rate` (Phase 16.2.1):
  // stand-alone services (visa/hotel/flight/attraction/activity) carry 0%
  // TCS regardless of location; only Custom + Readymade packages on
  // international locations get 2%.
  const _qt = (quote.quoteType || 'custom').toLowerCase();
  const _isStandalone = ['visa', 'hotel', 'flight', 'attraction', 'activity'].includes(_qt);
  // Directive 2 — defensive: don't offer "Add option" if pricingOptions
  // already contains entries that predate the QuoteOption shape (no optionId).
  const hasLegacyOptions = (quote.pricingOptions || []).some((o) => !o || !o.optionId);
  const tcsRateAuto = synced
    ? (parseFloat(quote.tcsRate) || 0)
    : ((!_isStandalone && (quote.locationType || 'domestic').toLowerCase() === 'international') ? 2 : 0);
  const gstAmt = baseFromItems * ((parseFloat(quote.gstRate) || 0) / 100);
  const tcsAmt = (baseFromItems + gstAmt) * (tcsRateAuto / 100);

  // Task 2 — pax-mismatch guard. Compare the configured headcount
  // (adults+children+infants) against the number of traveller entries
  // actually linked (primary guest + additional pax rows). A mismatch usually
  // means someone's profile/docs haven't been captured yet.
  const paxConfigured = Number(quote.adults || 0) + Number(quote.children || 0) + Number(quote.infants || 0);
  const paxLinked = (quote.guestName ? 1 : 0) + (Array.isArray(quote.additionalPax) ? quote.additionalPax.length : 0);
  const paxMismatch = paxConfigured > 0 && paxConfigured !== paxLinked;
  // Task 9 — discount + round-off, computed on the gross (after GST+TCS),
  // mirroring backend calculate_quote_totals so the preview matches on save.
  const _gross = baseFromItems + gstAmt + tcsAmt;
  const _discType = quote.discountType || null;
  const _discVal = Math.max(0, parseFloat(quote.discountValue) || 0);
  const _discAmt = _discType === 'percentage'
    ? (_gross * Math.min(_discVal, 100)) / 100
    : (_discType === 'amount' ? Math.min(_discVal, _gross) : 0);
  const _afterDisc = Math.max(0, _gross - _discAmt);
  const _roundTo = parseInt(quote.roundOffTo, 10) || 0;
  const _roundDir = quote.roundOffDir || 'nearest';
  const _rounded = _roundTo > 0
    ? (_roundDir === 'up'
        ? Math.ceil(_afterDisc / _roundTo) * _roundTo
        : _roundDir === 'down'
          ? Math.floor(_afterDisc / _roundTo) * _roundTo
          : Math.round(_afterDisc / _roundTo) * _roundTo)
    : _afterDisc;
  const _roundAdj = _rounded - _afterDisc;
  const totals = {
    baseCost: baseFromItems,
    gstAmount: gstAmt,
    tcsAmount: tcsAmt,
    grossTotal: _gross,
    discountAmount: _discAmt,
    roundOffAdjustment: _roundAdj,
    totalCost: _rounded,
  };

  // Task 1 — WhatsApp quick send. Outstanding = trip value minus receipts.
  const tripTotal = Number(quote.totalCost || totals.totalCost || 0);
  const tripReceived = (quote.receipts || []).reduce((s, r) => s + Number(r.amount ?? r.amountPaid ?? 0), 0);
  const tripOutstanding = Math.max(tripTotal - tripReceived, 0);
  const waPhone = waNumber(quote.countryCode || '+91', quote.phone);
  const openWhatsAppSend = async () => {
    if (!quote.phone) { toast.error('No phone on file for this trip'); return; }
    const pl = await resolveTripPaymentLink(quote.tripId);
    setWaPaymentLink(pl);
    setWaOpen(true);
  };

  // WhatsApp Path B — plain-text lump-sum quote message via a prefilled
  // wa.me link, bypassing the Meta Cloud API entirely (works even before any
  // template is approved). Same "no breakdown" pricing rule as every other
  // channel — uses the SELECTED pricing option's total when one exists,
  // else the trip-level total. NOTE: unlike "Send WhatsApp" above, a message
  // sent this way does NOT appear in the WhatsApp Chats log (that log only
  // tracks Cloud-API-sent/received messages) — it just opens WhatsApp with
  // the text ready to send.
  const needsOptionSelection = (q) => {
    const options = q?.pricingOptions || [];
    return options.length > 0 && !options.some((o) => o.isSelected);
  };
  const handleWhatsAppMessage = async () => {
    if (!quote.phone) { toast.error('No phone on file for this trip'); return; }
    if (needsOptionSelection(quote)) { toast.error('Select a pricing option before sending — no option is picked yet'); return; }
    const selected = (quote.pricingOptions || []).find((o) => o.isSelected);
    const price = Number(selected?.totals?.totalCost ?? quote.totalCost ?? 0);
    const dates = quote.travelStartDate && quote.travelEndDate
      ? `${quote.travelStartDate} to ${quote.travelEndDate}` : '';
    const CATEGORY_QUOTE_LABELS = { flight: 'Flight quote', hotel: 'Hotel quote', visa: 'Visa quote', attraction: 'Activity quote' };
    const categoryPhrase = CATEGORY_QUOTE_LABELS[(quote.quoteType || '').toLowerCase()] || 'Travel quote';
    const previewUrl = linkedItinerary
      ? (linkedItinerary.public_url || `${window.location.origin}/${linkedItinerary.public_ref || linkedItinerary.id}/preview`)
      : '';
    const lines = [
      `Hi ${quote.guestName || 'there'},`,
      `Here's your ${categoryPhrase} for ${quote.destination || 'your trip'}${dates ? ` (${dates})` : ''}.`,
      `Total: ₹${price.toLocaleString('en-IN')}`,
      previewUrl ? `View full details: ${previewUrl}` : '',
      `Let us know if you'd like to proceed!`,
    ].filter(Boolean);
    const text = lines.join('\n');
    window.open(waLink(quote.countryCode || '+91', quote.phone, text), '_blank', 'noopener,noreferrer');
    // WhatsApp Sent Log — best-effort; the wa.me tab has already opened
    // regardless of whether this log call succeeds.
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/mark-manual-sent`, {
        contextType: 'trip', contextId: quote.tripId, text,
      });
      toast.success('WhatsApp opened · logged in Chats as manually sent');
    } catch {
      toast.success('WhatsApp opened');
    }
  };

  // Flight text quote — same "no option selected" guard as Path B above.
  // Per-person fare = selected option's total ÷ actual traveller count
  // (never a fixed pax-mode assumption) — computed here, never read from
  // pasted flightDetails text.
  const flightSelected = (quote.pricingOptions || []).find((o) => o.isSelected);
  const selectedOptionTotal = Number(flightSelected?.totals?.totalCost ?? quote.totalCost ?? 0);
  const flightTotalPax = Math.max(1, Number(quote.adults || 0) + Number(quote.children || 0) + Number(quote.infants || 0));
  const flightFarePerPerson = selectedOptionTotal / flightTotalPax;
  const handleOpenFlightTextQuote = () => {
    if (!quote.phone) { toast.error('No phone on file for this trip'); return; }
    if (needsOptionSelection(quote)) { toast.error('Select a pricing option before sending — no option is picked yet'); return; }
    setFlightTextOpen(true);
  };

  // Hotel text quote — same guard, lump-sum total from the selected option.
  const handleOpenHotelTextQuote = () => {
    if (!quote.phone) { toast.error('No phone on file for this trip'); return; }
    if (needsOptionSelection(quote)) { toast.error('Select a pricing option before sending — no option is picked yet'); return; }
    setHotelTextOpen(true);
  };

  return (
    <div className="trip-edit-page max-w-6xl mx-auto space-y-6 pb-24 md:pb-0">
      {/* Compact mobile-first trip header (mobile only). Desktop unchanged. */}
      <div className="md:hidden">
        <TripMobileHeader
          quote={quote}
          linkedItinerary={linkedItinerary}
          onStatusChange={(newStatus) => setQuote((q) => ({ ...q, status: newStatus }))}
        />
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/quotes')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Primary: Edit / Create Itinerary */}
          <Link
            to={
              linkedItinerary
                ? `/itineraries/${linkedItinerary.id}`
                : `/itineraries/new?tripId=${quote.tripId}`
            }
          >
            <Button data-testid="manage-itinerary-btn" className="bg-teal-700 text-white">
              <MapPin className="h-4 w-4 mr-2" />
              {linkedItinerary ? 'Edit Itinerary' : 'Create Itinerary'}
            </Button>
          </Link>

          {/* Vouchers */}
          <Link to={`/vouchers?tripId=${quote.tripId}`}>
            <Button variant="outline" className="border-[#2C3E35]/30 text-[#2C3E35] hover:bg-[#2C3E35] hover:text-white" data-testid="trip-vouchers-btn">
              <Ticket className="h-4 w-4 mr-2" />Vouchers
            </Button>
          </Link>

          {/* Task 1 — WhatsApp quick send (template-driven) */}
          <Button variant="outline" onClick={openWhatsAppSend} className="border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white" data-testid="trip-send-whatsapp-btn">
            <MessageCircle className="h-4 w-4 mr-2" />Send WhatsApp
          </Button>

          {/* Compact 3-dot menu — View / Share / Invoice / Payment / Completed / Duplicate / Reassign */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="trip-more-menu" aria-label="More trip actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {linkedItinerary && (
                <DropdownMenuItem onClick={openItineraryNewTab} data-testid="view-itinerary-btn">
                  <Eye className="h-4 w-4 mr-2" /> View itinerary
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleSharePreview} data-testid="share-preview-btn">
                <Share2 className="h-4 w-4 mr-2" /> Share preview (opens new tab)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleWhatsAppMessage} data-testid="whatsapp-message-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp message (lump-sum, opens wa.me)
              </DropdownMenuItem>
              {_qt === 'flight' && (
                <DropdownMenuItem onClick={handleOpenFlightTextQuote} data-testid="flight-text-quote-menu-btn">
                  <Plane className="h-4 w-4 mr-2" /> Flight text quote (segments + fare)
                </DropdownMenuItem>
              )}
              {_qt === 'hotel' && (
                <DropdownMenuItem onClick={handleOpenHotelTextQuote} data-testid="hotel-text-quote-menu-btn">
                  <MapPin className="h-4 w-4 mr-2" /> Hotel text quote (stay + total)
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate(`/invoices?tripId=${quote.tripId}`)} data-testid="trip-invoices-btn">
                <ReceiptText className="h-4 w-4 mr-2" /> Invoice
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); handleTogglePayment(); }}
                disabled={togglingPayment}
                data-testid="payment-toggle-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {togglingPayment ? 'Updating…' : paymentEnabled ? 'Payment ON — tap to disable' : 'Payment OFF — tap to enable'}
              </DropdownMenuItem>
              {(quote.status || '').toLowerCase() !== 'completed' && (
                <DropdownMenuItem onClick={handleMarkCompleted} data-testid="mark-completed-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Completed
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleDuplicate} data-testid="duplicate-trip-btn">
                <Copy className="h-4 w-4 mr-2" /> Duplicate trip
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => setShowReassign(true)} data-testid="reassign-trip-menu-item">
                  <Users className="h-4 w-4 mr-2" /> Reassign to traveller
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SendWhatsAppDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        title={quote.guestName || quote.destination || 'Trip'}
        phone={waPhone}
        context={buildTripWaContext(quote, { itinerary: linkedItinerary, user, outstanding: tripOutstanding, paymentLink: waPaymentLink })}
        suggestHint={tripSuggestHint(quote, tripOutstanding)}
      />

      <FlightTextQuoteDialog
        open={flightTextOpen}
        onOpenChange={setFlightTextOpen}
        quote={quote}
        flightDetails={linkedItinerary?.flightDetails}
        farePerPerson={flightFarePerPerson}
      />

      <HotelTextQuoteDialog
        open={hotelTextOpen}
        onOpenChange={setHotelTextOpen}
        quote={quote}
        hotels={linkedItinerary?.hotels}
        totalCost={selectedOptionTotal}
      />

      <CancelTripDialog
        tripId={id}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onDone={fetchData}
      />

      {isAdmin && (
        <ReassignTripDialog
          open={showReassign}
          onOpenChange={setShowReassign}
          trip={quote}
          onDone={(_res, newTraveller) => {
            setQuote((q) => ({
              ...q,
              clientUCC: newTraveller?.ucc || q.clientUCC,
              guestName: newTraveller?.fullName || q.guestName,
            }));
          }}
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-slate-900 tracking-tight" data-testid="trip-edit-heading">
            {quote.destination || quote.guestName || 'Trip'}
            {(Number(quote.adults || 0) + Number(quote.children || 0)) > 0 && (
              <span className="text-slate-400 font-normal"> · {Number(quote.adults || 0) + Number(quote.children || 0)} pax</span>
            )}
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            {quote.guestName}
            {quote.displayTripId && <span className="text-slate-400"> · {quote.displayTripId}</span>}
            {linkedItinerary && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <FileText className="h-3 w-3 mr-1" />
                Itinerary Linked
              </span>
            )}
          </p>
        </div>
        {(quote.quoteStatus || 'active') === 'active' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setSupersedeReason(''); setSupersedeError(''); setShowSupersedeModal(true); }}
            data-testid="create-new-version-btn"
          >
            Create new version
          </Button>
        )}
      </div>

      <VersionHistoryPanel
        versions={familyVersions}
        currentTripId={quote.tripId}
        onSelect={(v) => navigate(`/quotes/${v.tripId}/edit`)}
      />

      {showSupersedeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="supersede-modal">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Create a new version?</h3>
            <p className="text-sm text-slate-600 mb-4">This will supersede the current version. Enter a reason (optional).</p>
            <Textarea
              value={supersedeReason}
              onChange={(e) => setSupersedeReason(e.target.value)}
              placeholder="e.g. Client requested a 4★ hotel instead of 3★"
              className="mb-2"
              data-testid="supersede-reason-input"
            />
            {supersedeError && (
              <p className="text-sm text-red-600 mb-2" data-testid="supersede-error">{supersedeError}</p>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowSupersedeModal(false)} data-testid="supersede-cancel-btn">
                Cancel
              </Button>
              <Button type="button" size="sm" disabled={superseding} onClick={handleSupersede} data-testid="supersede-confirm-btn">
                {superseding ? 'Creating…' : 'Confirm'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {paxMismatch && (
        <div
          className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800"
          data-testid="pax-mismatch-banner"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-sm">
            Trip configured for <span className="font-semibold">{paxConfigured} pax</span> but only{' '}
            <span className="font-semibold">{paxLinked} traveller {paxLinked === 1 ? 'profile' : 'profiles'} linked</span>.
            {' '}Add the remaining travellers under the Travellers tab.
          </p>
        </div>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto md:grid md:grid-cols-5 [&>*]:shrink-0" style={{ scrollbarWidth: 'none' }}>
          <TabsTrigger value="details" data-testid="tab-trip-details">Trip Details</TabsTrigger>
          <TabsTrigger value="travellers" data-testid="tab-travellers">Travellers</TabsTrigger>
          <TabsTrigger value="hotels" data-testid="tab-hotels">Hotels</TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">Policies</TabsTrigger>
          <TabsTrigger value="financials" data-testid="tab-financials">Financials</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 mt-6">
          <Card className="p-6" data-testid="trip-followups-card">
            <FollowUpManager tripId={quote.tripId} />
          </Card>
          <AiBriefPanel contextType="trip" contextId={quote.tripId} title={quote.destination} />
          <form onSubmit={handleQuoteUpdate} className="space-y-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <h2 className="text-2xl font-heading font-semibold">Guest Information</h2>
                {quote.clientUCC && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={autoFetching}
                    onClick={autoFetchTravellerContact}
                    data-testid="auto-fetch-contact-btn"
                    className="h-8 text-xs"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${autoFetching ? 'animate-spin' : ''}`} />
                    Auto-fetch from traveller
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Guest Name</Label>
                  <Input
                    value={quote.guestName}
                    onChange={(e) => setQuote({...quote, guestName: e.target.value})}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={quote.email}
                      onChange={(e) => setQuote({...quote, email: e.target.value})}
                      data-testid="quote-email-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      onClick={() => copyField(quote.email, 'Email')}
                      data-testid="copy-email-btn"
                      title="Copy email"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={quote.phone}
                      onChange={(e) => setQuote({...quote, phone: e.target.value})}
                      data-testid="quote-phone-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      onClick={() => copyField(quote.phone, 'Phone')}
                      data-testid="copy-phone-btn"
                      title="Copy phone"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Departure City Dropdown */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Plane className="h-4 w-4 rotate-45" /> Departure City
                  </Label>
                  <select
                    value={quote.departureCity || ''}
                    onChange={(e) => setQuote({...quote, departureCity: e.target.value})}
                    className="mt-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="departure-city-edit-select"
                  >
                    <option value="">Select City</option>
                    {departureCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                    {quote.departureCity && !departureCities.includes(quote.departureCity) && (
                      <option value={quote.departureCity}>{quote.departureCity}</option>
                    )}
                  </select>
                </div>
                
                {/* Destination Dropdown */}
                <div className="md:col-span-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Destination
                  </Label>
                  <select
                    value={quote.destination || ''}
                    onChange={(e) => setQuote({...quote, destination: e.target.value})}
                    className="mt-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="destination-edit-select"
                  >
                    <option value="">Select Destination</option>
                    {destinations.map(dest => (
                      <option key={dest} value={dest}>{dest}</option>
                    ))}
                    {quote.destination && !destinations.includes(quote.destination) && (
                      <option value={quote.destination}>{quote.destination}</option>
                    )}
                  </select>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-heading font-semibold mb-6">Pricing</h2>

              {/* BATCH 2C — Quote Currency Mode (Admin/Accounts only). */}
              <div className="mb-6">
                <QuoteCurrencyMode
                  value={quote}
                  onChange={(patch) => setQuote((prev) => ({ ...prev, ...patch }))}
                  canFx={canFx}
                  travelStartDate={quote.travelStartDate}
                />
              </div>

              {synced && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="priced-from-itinerary-banner">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">Priced from itinerary</span>
                    {quote.pricing_synced_at && (
                      <span className="text-xs text-amber-600">
                        · synced {new Date(quote.pricing_synced_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={handleSyncPricing} data-testid="sync-pricing-btn">
                      Sync now
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={handleUnlockPricing} data-testid="edit-manually-btn">
                      Edit manually
                    </Button>
                  </div>
                </div>
              )}

              {/* Never-synced-yet trip with a linked itinerary: still expose the
                  sync action so the 409-confirm guard (Task A) is reachable
                  even before the first sync, e.g. a trip that already has
                  vendor cost items entered manually. */}
              {!synced && linkedItinerary && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="not-synced-itinerary-banner">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText className="h-4 w-4" />
                    <span>This quote has a linked itinerary — pricing has not been synced from it yet.</span>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={handleSyncPricing} data-testid="sync-pricing-btn">
                    Sync from itinerary
                  </Button>
                </div>
              )}

              {/* Location type + Quote type together drive the TCS rule
                  (Phase 16.2.1): 2% only when International AND quoteType
                  is custom/readymade package. Stand-alone services are 0%. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <Label>Location Type</Label>
                  <Select
                    value={(quote.locationType || 'domestic').toLowerCase()}
                    disabled={synced}
                    onValueChange={(value) => {
                      const qt = (quote.quoteType || 'custom').toLowerCase();
                      const standalone = ['visa', 'hotel', 'flight', 'attraction', 'activity'].includes(qt);
                      const newTcs = (!standalone && value === 'international') ? 2 : 0;
                      setQuote({ ...quote, locationType: value, tcsRate: newTcs });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="location-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic (India)</SelectItem>
                      <SelectItem value="international">International</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="tcs-auto-hint">
                    TCS auto: {tcsRateAuto === 2 ? '2% (international package)' : 'Not applicable'}
                  </p>
                </div>
                <div>
                  <Label>Quote Type</Label>
                  <Select
                    value={(quote.quoteType || 'custom').toLowerCase()}
                    disabled={synced}
                    onValueChange={(value) => {
                      const standalone = ['visa', 'hotel', 'flight', 'attraction', 'activity'].includes(value);
                      const newTcs = (!standalone && (quote.locationType || '').toLowerCase() === 'international') ? 2 : 0;
                      setQuote({ ...quote, quoteType: value, tcsRate: newTcs });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="quote-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom Package</SelectItem>
                      <SelectItem value="readymade">Readymade Package</SelectItem>
                      <SelectItem value="flight">Flight Only</SelectItem>
                      <SelectItem value="hotel">Hotel Only</SelectItem>
                      <SelectItem value="attraction">Attraction / Activity</SelectItem>
                      <SelectItem value="visa">Visa Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>GST Rate</Label>
                  <Select value={quote.gstRate?.toString()} disabled={synced} onValueChange={(value) => setQuote({...quote, gstRate: parseFloat(value)})}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (Exempt)</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="18">18%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Quote Expiry Date</Label>
                      {isQuoteExpired(quote) && (
                        <span
                          data-testid="quote-expired-badge"
                          className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700 border border-rose-200"
                        >
                          Expired
                        </span>
                      )}
                      <Input
                        type="date"
                        value={quote.expiryDate || ''}
                        onChange={(e) => setQuote({ ...quote, expiryDate: e.target.value })}
                        className="mt-2"
                        data-testid="expiry-date-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        After this date the quote is treated as expired. Leave blank for no expiry.
                      </p>
                    </div>
                    <div>
                      <Label>Base Cost (Auto from Items)</Label>
                      <Input
                        type="number"
                        value={quote.baseCost || 0}
                        onChange={(e) => setQuote({...quote, baseCost: e.target.value})}
                        className="mt-2"
                        disabled={synced || (Array.isArray(quote.costItems) && quote.costItems.length > 0)}
                        data-testid="base-cost-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        {synced
                          ? 'Locked — priced from the linked itinerary'
                          : (Array.isArray(quote.costItems) && quote.costItems.length > 0
                            ? 'Locked — sum of cost items below'
                            : 'Manual entry (no cost items added)')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Item-wise cost breakdown — multi-currency, margin per item.
                  Hidden when synced: the itinerary is the source of truth. */}
              {!synced && (
                <>
                  {['confirmed', 'accepted', 'completed', 'closed'].includes((quote.status || '').toLowerCase()) && (
                    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3" data-testid="cost-items-locked-hint">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">This quote is finalised — cost items and their commercial terms are locked. Saving with any change to them will be rejected.</p>
                    </div>
                  )}
                  <CostBreakdown
                    value={quote.costItems || []}
                    onChange={(next) => setQuote({ ...quote, costItems: next })}
                    onTotalChange={(total) => {
                      if (Array.isArray(quote.costItems) && quote.costItems.length > 0) {
                        setQuote((q) => ({ ...q, baseCost: total }));
                      }
                    }}
                  />
                  <div className="flex items-center justify-between gap-4 mt-4 p-4 rounded-xl border border-slate-200" data-testid="flight-separate-toggle-row">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Show flight cost separately to client</p>
                      <p className="text-xs text-slate-500 mt-0.5">Off (default): one combined lump-sum total. On: client sees "Package" and "Flights" as two lines (still lump-sum each, never itemised by vendor).</p>
                    </div>
                    <Switch
                      checked={!!quote.showFlightSeparately}
                      onCheckedChange={(v) => setQuote({ ...quote, showFlightSeparately: v })}
                      data-testid="flight-separate-toggle"
                    />
                  </div>
                </>
              )}

              <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-100" data-testid="agent-totals-card">
                <p className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-3">Agent View — Full Breakdown</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Cost (sum of items):</span>
                    <span className="font-medium">₹{totals.baseCost.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({quote.gstRate}%):</span>
                    <span className="font-medium">₹{totals.gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>TCS ({tcsRateAuto}%) {tcsRateAuto === 2 ? '— International package' : '— N/A'}:</span>
                    <span className="font-medium">₹{totals.tcsAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>

                  {/* Task 9 — Discount + Round-off controls */}
                  <div className="pt-3 mt-1 border-t border-teal-200 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="quote-discount-roundoff">
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Discount</label>
                      <div className="flex gap-2 mt-1">
                        <select
                          value={quote.discountType || 'none'}
                          onChange={(e) => setQuote({ ...quote, discountType: e.target.value === 'none' ? null : e.target.value })}
                          className="h-9 rounded-md border border-slate-200 text-sm bg-white px-2 text-slate-700"
                          data-testid="quote-discount-type"
                        >
                          <option value="none">None</option>
                          <option value="amount">₹ Amount</option>
                          <option value="percentage">% Percent</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          disabled={!quote.discountType}
                          value={quote.discountValue ?? ''}
                          onChange={(e) => setQuote({ ...quote, discountValue: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })}
                          placeholder="0"
                          className="h-9 w-24 rounded-md border border-slate-200 text-sm px-2 disabled:bg-slate-50"
                          data-testid="quote-discount-value"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Round off</label>
                      <div className="flex gap-2 mt-1">
                        <select
                          value={String(quote.roundOffTo || 0)}
                          onChange={(e) => setQuote({ ...quote, roundOffTo: parseInt(e.target.value, 10) || 0 })}
                          className="h-9 rounded-md border border-slate-200 text-sm bg-white px-2 text-slate-700"
                          data-testid="quote-roundoff-to"
                        >
                          <option value="0">Off</option>
                          <option value="1">Nearest ₹1</option>
                          <option value="5">Nearest ₹5</option>
                          <option value="10">Nearest ₹10</option>
                          <option value="100">Nearest ₹100</option>
                        </select>
                        <select
                          value={quote.roundOffDir || 'nearest'}
                          disabled={!quote.roundOffTo}
                          onChange={(e) => setQuote({ ...quote, roundOffDir: e.target.value })}
                          className="h-9 rounded-md border border-slate-200 text-sm bg-white px-2 text-slate-700 disabled:bg-slate-50"
                          data-testid="quote-roundoff-dir"
                        >
                          <option value="nearest">Nearest</option>
                          <option value="up">Round up</option>
                          <option value="down">Round down</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-rose-600" data-testid="quote-discount-line">
                      <span>Discount{_discType === 'percentage' ? ` (${_discVal}%)` : ''}:</span>
                      <span className="font-medium">− ₹{totals.discountAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {Math.abs(totals.roundOffAdjustment) >= 0.005 && (
                    <div className="flex justify-between text-slate-500" data-testid="quote-roundoff-line">
                      <span>Round off:</span>
                      <span className="font-medium">{totals.roundOffAdjustment >= 0 ? '+' : '−'} ₹{Math.abs(totals.roundOffAdjustment).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-teal-200">
                    <span className="font-semibold">Final Price (Client-Visible):</span>
                    <span className="font-bold text-lg text-teal-700" data-testid="quote-grand-total">₹{totals.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Directive 2 (2026-09-21) — V1 dynamic multi-option quote editor.
                Standalone quotes only (same check as GST branching above). */}
            {_isStandalone && (
              <Card className="p-6" data-testid="option-editor-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-heading font-semibold">Pricing Options</h2>
                    <p className="text-sm text-slate-500 mt-1">Offer the client 2 or more alternatives (e.g. Economy vs Business) in this same quote.</p>
                  </div>
                  {!hasLegacyOptions && (
                    <Button type="button" variant="outline" size="sm" onClick={addOption} data-testid="add-option-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add option
                    </Button>
                  )}
                </div>

                {(quote.pricingOptions || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic" data-testid="no-options-placeholder">No options yet — add one to offer alternatives on this quote.</p>
                ) : (
                  <>
                    {(quote.pricingOptions || []).some((o) => o.isSelected) && (
                      <div className="mb-4 rounded-lg bg-teal-50 border border-teal-200 px-4 py-2 text-sm text-teal-800" data-testid="option-selected-banner">
                        Option selected: <span className="font-semibold">
                          {(quote.pricingOptions || []).find((o) => o.isSelected)?.label || 'Untitled option'}
                        </span>
                      </div>
                    )}
                    <div className="space-y-4">
                    {(quote.pricingOptions || []).map((option, idx) => {
                      const canRemove = !(option.isSelected && (quote.pricingOptions || []).length > 1);
                      const anySelected = (quote.pricingOptions || []).some((o) => o.isSelected);
                      return (
                        <div
                          key={option.optionId || idx}
                          className={`rounded-xl border-2 p-4 transition ${option.isSelected ? 'border-teal-400 bg-teal-50/50 ring-1 ring-teal-200' : 'border-slate-200 bg-white'}`}
                          data-testid={`option-card-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-500">Label</Label>
                                <Input
                                  value={option.label || ''}
                                  onChange={(e) => updateOptionField(option.optionId, 'label', e.target.value)}
                                  placeholder="e.g. 3-Star, 4-Star, 5-Star"
                                  className="mt-1"
                                  data-testid={`option-label-input-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Summary</Label>
                                <Input
                                  value={option.summary || ''}
                                  onChange={(e) => updateOptionField(option.optionId, 'summary', e.target.value)}
                                  placeholder="One line — e.g. Beachfront resort, breakfast included"
                                  className="mt-1"
                                  data-testid={`option-summary-input-${idx}`}
                                />
                              </div>
                            </div>
                            {option.isSelected && (
                              <span className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800" data-testid={`option-selected-badge-${idx}`}>
                                Selected
                              </span>
                            )}
                          </div>

                          <CostBreakdown
                            value={option.costItems}
                            onChange={(next) => updateOptionField(option.optionId, 'costItems', next)}
                          />

                          <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-100 text-sm" data-testid={`option-totals-${idx}`}>
                            {option.totals ? (
                              <div className="flex flex-wrap gap-x-6 gap-y-1">
                                <span>Base: <span className="font-medium">₹{Number(option.totals.baseCost || 0).toLocaleString('en-IN')}</span></span>
                                <span>GST ({option.totals.gstRate}%): <span className="font-medium">₹{Number(option.totals.gstAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></span>
                                <span>TCS ({option.totals.tcsRate}%): <span className="font-medium">₹{Number(option.totals.tcsAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></span>
                                <span>Total: <span className="font-bold text-teal-700">₹{Number(option.totals.totalCost || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></span>
                              </div>
                            ) : (
                              <span className="text-slate-400 italic" data-testid={`option-save-to-compute-${idx}`}>Save to compute totals</span>
                            )}
                          </div>

                          <div className="mt-3 flex items-center gap-2">
                            {!option.isSelected && (
                              <Button type="button" size="sm" variant="outline" onClick={() => selectOption(option.optionId)} data-testid={`option-select-btn-${idx}`}>
                                Select this option
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={!canRemove}
                              title={!canRemove ? 'Select another option before removing this one' : undefined}
                              onClick={() => removeOption(option.optionId)}
                              data-testid={`option-remove-btn-${idx}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove option
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}
              </Card>
            )}

            {/* Trip Status + Travel Dates — controls Dashboard upcoming-departures
                and confirmed-revenue filters. */}
            <Card className="p-6" data-testid="trip-status-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-semibold">Trip Status & Dates</h2>
                {!['cancelled', 'completed'].includes((quote.status || '').toLowerCase()) && (
                  <Button
                    type="button" variant="outline" size="sm"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => setCancelDialogOpen(true)}
                    data-testid="cancel-trip-btn"
                  >
                    Cancel Trip
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={(quote.status || 'draft').toLowerCase()}
                    onValueChange={(value) => {
                      if (value === 'cancelled') { setCancelDialogOpen(true); return; }
                      setQuote({ ...quote, status: value });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="trip-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Confirmed / Accepted / Completed count towards realised revenue & upcoming departures.
                  </p>
                </div>
                <div>
                  <Label>Travel Start Date</Label>
                  <Input
                    type="date"
                    value={quote.travelStartDate || ''}
                    onChange={(e) => setQuote({ ...quote, travelStartDate: e.target.value })}
                    className="mt-2"
                    data-testid="travel-start-date-input"
                  />
                </div>
                <div>
                  <Label>Travel End Date</Label>
                  <Input
                    type="date"
                    value={quote.travelEndDate || ''}
                    onChange={(e) => setQuote({ ...quote, travelEndDate: e.target.value })}
                    className="mt-2"
                    data-testid="travel-end-date-input"
                  />
                </div>
              </div>
              {linkedItinerary && (!quote.travelStartDate || !quote.travelEndDate) && (
                <p className="text-[11px] text-amber-600 mt-3" data-testid="auto-populate-hint">
                  Linked itinerary detected — saving the itinerary will auto-populate empty travel dates from it.
                </p>
              )}
            </Card>

            <div className="hidden md:flex justify-end">
              <Button type="submit" disabled={saving} data-testid="save-changes-btn" className="bg-teal-700 text-white">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="travellers" className="space-y-6 mt-6">
          <Card className="p-6" data-testid="travellers-tab-panel">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-teal-700" />
              <h2 className="text-xl font-heading font-semibold">Travellers on this trip</h2>
            </div>
            <p className="text-[12px] text-slate-500 mb-5">
              Search to link an existing traveller, add family / co-travellers, and set each one&rsquo;s visa status for {quote.destination || 'this destination'}.
            </p>

            {/* Primary traveller + their visa status for THIS trip */}
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 mb-5">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-wide text-teal-700 font-semibold">Primary traveller</span>
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid="primary-traveller-name">
                    {quote.guestName || '—'}
                  </div>
                </div>
                <div className="w-full sm:w-56">
                  <label className="text-[11px] text-slate-500 mb-1 block">Visa status</label>
                  <VisaStatusSelect
                    value={quote.primaryVisaStatus || ''}
                    onChange={(v) => setQuote({ ...quote, primaryVisaStatus: v })}
                    testid="primary-visa-status"
                  />
                </div>
              </div>
            </div>

            <AdditionalPaxEditor
              value={quote.additionalPax || []}
              onChange={(next) => setQuote({ ...quote, additionalPax: next })}
              showVisa
            />

            <div className="flex justify-end mt-6">
              <Button onClick={handleQuoteUpdate} disabled={saving} data-testid="save-travellers-btn" className="bg-teal-700 text-white">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving…' : 'Save Travellers'}
              </Button>
            </div>
          </Card>

          {quote.clientUCC && quote.tripId && (
            <div className="mt-6">
              <TravellerVisaDocs
                ucc={quote.clientUCC}
                tripId={quote.tripId}
                tripLabel={`${quote.destination || 'Trip'} · ${quote.displayTripId || quote.tripId}`}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="hotels" className="space-y-6 mt-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-semibold">Hotels</h2>
              <Button onClick={handleAddHotel} data-testid="add-hotel-btn" className="bg-teal-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add Hotel
              </Button>
            </div>

            {hotels.length === 0 ? (
              <div className="text-center py-8 text-slate-500">No hotels added yet</div>
            ) : (
              <div className="space-y-4">
                {hotels.map((hotel) => (
                  <div key={hotel.id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input value={hotel.city} className="mt-1" readOnly />
                      </div>
                      <div>
                        <Label className="text-xs">Hotel Name</Label>
                        <Input value={hotel.hotelName} className="mt-1" readOnly />
                      </div>
                      <div>
                        <Label className="text-xs">Room Type</Label>
                        <Input value={hotel.roomType} className="mt-1" readOnly />
                      </div>
                      <div>
                        <Label className="text-xs">Meal Plan</Label>
                        <Input value={hotel.mealPlan} className="mt-1" readOnly />
                      </div>
                      <div className="flex items-end">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteHotel(hotel.id)}
                          data-testid={`delete-hotel-${hotel.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="space-y-6 mt-6">
          <Card className="p-6">
            <h2 className="text-2xl font-heading font-semibold mb-6">Policies & Terms</h2>
            
            <div className="space-y-6">
              <div>
                <Label>Inclusions</Label>
                <Textarea
                  value={policies.inclusions}
                  onChange={(e) => setPolicies({...policies, inclusions: e.target.value})}
                  rows={4}
                  className="mt-2"
                  placeholder="What's included in the package..."
                />
              </div>
              
              <div>
                <Label>Exclusions</Label>
                <Textarea
                  value={policies.exclusions}
                  onChange={(e) => setPolicies({...policies, exclusions: e.target.value})}
                  rows={4}
                  className="mt-2"
                  placeholder="What's not included..."
                />
              </div>
              
              <div>
                <Label>Payment Policy</Label>
                <Textarea
                  value={policies.paymentPolicy}
                  onChange={(e) => setPolicies({...policies, paymentPolicy: e.target.value})}
                  rows={4}
                  className="mt-2"
                  placeholder="Payment terms and conditions..."
                />
              </div>
              
              <div>
                <Label>Cancellation Policy</Label>
                <Textarea
                  value={policies.cancellationPolicy}
                  onChange={(e) => setPolicies({...policies, cancellationPolicy: e.target.value})}
                  rows={4}
                  className="mt-2"
                  placeholder="Cancellation and refund policy..."
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSavePolicies} data-testid="save-policies-btn" className="bg-teal-700 text-white">
                  <Save className="h-4 w-4 mr-2" />
                  Save Policies
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="financials" className="mt-6">
          <TripFinancials tripId={quote.tripId} tripRevenue={quote.totalCost} trip={quote} />
        </TabsContent>
      </Tabs>

      {/* Mobile-only sticky Save bar (desktop unchanged) */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 px-4 py-3" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }} data-testid="m-trip-save-bar">
        <Button type="button" onClick={() => handleQuoteUpdate()} disabled={saving} data-testid="m-trip-save-btn" className="w-full h-12 bg-teal-700 text-white text-base">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : 'Save Trip'}
        </Button>
      </div>
    </div>
  );
};

export default EditQuote;
